import {
  ArrowsInSimple,
  DownloadSimple,
  FileCsv,
  FileImage,
  FilePdf,
} from "@phosphor-icons/react";
import { Button, Flex, Text } from "@radix-ui/themes";

import { CanvasBlockRenderer } from "./canvas/CanvasBlockRenderer";
import { SandboxedRenderer } from "./canvas/SandboxedRenderer";
import { type CanvasContent } from "./canvas/types";

import { useAnalytics } from "../../../utils/analytics";

interface CanvasPaneProps {
  canvas: CanvasContent;
  onDismiss: () => void;
  skillName?: string;
  chatId?: string;
}

const OUTPUT_TYPE_LABELS: Record<string, string> = {
  risk_assessment: "Risk Assessment",
  brief_document: "Creative Brief",
  comparison: "Comparison",
  report: "Report",
  visualization: "Visualization",
  custom: "Output",
};

const EXPORT_ICONS: Record<string, React.ReactNode> = {
  pdf: <FilePdf size={14} />,
  csv: <FileCsv size={14} />,
  png: <FileImage size={14} />,
};

export function CanvasPane({
  canvas,
  onDismiss,
  skillName,
  chatId,
}: CanvasPaneProps) {
  const analytics = useAnalytics("ask_puntt_canvas");
  const exportFormats = canvas.exportFormats ?? [];
  const label =
    OUTPUT_TYPE_LABELS[canvas.outputType] ?? OUTPUT_TYPE_LABELS.custom;

  const handleExport = async (format: string) => {
    analytics.capture("canvas_exported", {
      output_type: canvas.outputType,
      skill_name: skillName,
      export_format: format,
      chat_id: chatId,
    });

    if (format === "csv") {
      // Serialize table blocks to CSV
      const tables = canvas.blocks.filter(
        (b) => (b as { type: string }).type === "table",
      );
      if (tables.length > 0) {
        const table = tables[0] as {
          headers: string[];
          rows: string[][];
        };
        const csvContent = [
          table.headers.join(","),
          ...table.rows.map((row) =>
            row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","),
          ),
        ].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${label.toLowerCase().replace(/\s+/g, "_")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }

    if (format === "png") {
      try {
        // html2canvas is an optional dependency — use opaque import to
        // prevent Rollup from failing the build when it's not installed.
        const pkg = "html2canvas";
        const mod = await import(/* @vite-ignore */ pkg);
        const html2canvas = mod.default;
        const el = document.getElementById("canvas-content");
        if (el) {
          const c = await html2canvas(el);
          const url = c.toDataURL("image/png");
          const a = document.createElement("a");
          a.href = url;
          a.download = `${label.toLowerCase().replace(/\s+/g, "_")}.png`;
          a.click();
        }
      } catch {
        console.error("PNG export failed — install html2canvas");
      }
    }

    if (format === "pdf") {
      try {
        // html2pdf.js is an optional dependency — use opaque import to
        // prevent Rollup from failing the build when it's not installed.
        const pkg = "html2pdf.js";
        const mod = await import(/* @vite-ignore */ pkg);
        const html2pdf = mod.default;
        const el = document.getElementById("canvas-content");
        if (el) {
          html2pdf()
            .set({
              margin: 10,
              filename: `${label.toLowerCase().replace(/\s+/g, "_")}.pdf`,
            })
            .from(el)
            .save();
        }
      } catch {
        console.error("PDF export failed — install html2pdf.js");
      }
    }
  };

  return (
    <div className="flex h-full flex-col border-l border-puntt-accent-6/20 bg-puntt-neutral-gray-1">
      {/* Header */}
      <Flex
        align="center"
        justify="between"
        className="shrink-0 border-b border-puntt-neutral-gray-5 bg-base-white px-5 py-4"
      >
        <Flex align="center" gap="2">
          <div className="size-2.5 animate-pulse rounded-full bg-puntt-accent-9 ring-2 ring-puntt-accent-4" />
          <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
            {label}
          </Text>
          {skillName && (
            <Text size="1" className="text-puntt-neutral-gray-9">
              via {skillName}
            </Text>
          )}
        </Flex>
        <Flex
          align="center"
          gap="1"
          className="rounded-lg bg-puntt-neutral-gray-2 px-2 py-1"
        >
          {exportFormats.map((format) => (
            <Button
              key={format}
              size="1"
              variant="ghost"
              onClick={() => handleExport(format)}
              className="cursor-pointer"
            >
              {EXPORT_ICONS[format] ?? <DownloadSimple size={14} />}
              <Text size="1">{format.toUpperCase()}</Text>
            </Button>
          ))}
          <Button
            size="1"
            variant="ghost"
            onClick={onDismiss}
            className="cursor-pointer"
          >
            <ArrowsInSimple size={14} />
          </Button>
        </Flex>
      </Flex>

      {/* Content */}
      <div id="canvas-content" className="flex-1 overflow-y-auto p-6">
        {canvas.rawHtml ? (
          <SandboxedRenderer html={canvas.rawHtml} />
        ) : (
          <div className="space-y-4">
            {canvas.blocks.map((block, idx) => (
              <div
                key={idx}
                className="animate-block-fade-up"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <CanvasBlockRenderer block={block} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
